import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadNavigationGuard } from "../../../hooks/useUploadNavigationGuard";
import axios from "axios";
import { toast } from "react-toastify";
import { ArrowLeft, Building2, Loader2 } from "lucide-react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Label } from "../../misc/Label";
import { Switch } from "../../misc/Switch";
import { Button } from "../../misc/Button";
import SimpleTooltip from "../../misc/SimpleTooltip";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import CustomToast from "../../misc/CustomToast";
import { getAuthHeader } from "../../../utils/authHeader";
import { setUser } from "../../../store/authSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { useImpersonation } from "../../../hooks/useImpersonation";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { cn } from "../../../utils/classMerger";
import MlsPropertyTypesSection from "./MlsPropertyTypesSection";
import {
    getMlsPreferences,
    updateMlsPreferences,
} from "../../listings/mlsPropertyTypesApi";
import { refreshMlsCandidates } from "../../listings/mlsCandidatesApi";
import { MlsPropertyTypeOption } from "../../../types/mlsPropertyTypes";
import {
    AUTOPILOT_BUSY_POLL_MS,
    CLEAR_MLS_BUSY_TOOLTIP,
} from "../../../types/autopilot";

interface ReadOnlyFieldProps {
    label: string;
    value?: string;
}

// MLS-derived identity is read-only — captured from the user's MLS listings.
const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <Label className="font-semibold text-primary">{label}</Label>
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm min-h-[42px] flex items-center text-primary break-words">
            {value ? value : <span className="text-gray-400">—</span>}
        </div>
    </div>
);

const sameTokens = (a: string[], b: Set<string>): boolean => {
    if (a.length !== b.size) return false;
    return a.every((token) => b.has(token));
};

// MB-1398: `embedded` renders the page inside a dialog (the MLS setup wizard's
// agent-profile overlay) — navigation actions become onClose so the wizard
// underneath never unmounts and staged choices survive.
interface MlsDetailsProps {
    embedded?: boolean;
    onClose?: () => void;
}

const MlsDetails: React.FC<MlsDetailsProps> = ({
    embedded = false,
    onClose,
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile(1200);
    const isImpersonated = useImpersonation();

    const user = useAppSelector((state) => state.auth.user);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // An MLS account is "connected" once an agent id has been captured. Identity
    // is only set by importing an MLS listing, so it implies MLS approval.
    const isMlsApproved = user?.mlsApproved ?? false;
    const hasMlsAccount = !!user?.listAgentMlsId?.trim();
    // MB-1398: the wizard's overlay is a VIEW ("View MLS Agent Profile"). Editing
    // from there fought the wizard itself: turning auto-sync off invalidated the
    // autopilot prerequisite the wizard had already staged, so its final Save
    // failed with "Missing: autoIngestEnabled" and neither footer button could
    // recover; and property types are editable in wizard step 2, so whichever
    // was saved here was overwritten by the staged picks moments later.
    const canEdit = isMlsApproved && !isImpersonated && !embedded;

    // Staged MLS Agent Profile preferences (auto-sync toggle + property types). Nothing is
    // persisted until Save; `savedRef` holds the last-saved values for dirty detection.
    const [autoIngestEnabled, setAutoIngestEnabled] = useState(false);
    const [propertyTypeOptions, setPropertyTypeOptions] = useState<
        MlsPropertyTypeOption[]
    >([]);
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
    const [loadingPrefs, setLoadingPrefs] = useState(true);
    const [saving, setSaving] = useState(false);
    const savedRef = useRef<{ autoIngestEnabled: boolean; selected: string[] }>(
        { autoIngestEnabled: false, selected: [] }
    );

    const [clearOverlay, setClearOverlay] = useState(false);
    const [clearing, setClearing] = useState(false);
    // Whether Autopilot is currently on — drives how hard the clear-MLS warning
    // is worded (clearing the agent force-disables Autopilot and deletes its
    // listings). null = unknown (fetch failed), falls back to conditional copy.
    const [autopilotOn, setAutopilotOn] = useState<boolean | null>(null);
    // MB-1532: Clear MLS is refused (423) while Auto Pilot is still setting up.
    const [autopilotBusy, setAutopilotBusy] = useState(false);
    // Until the first fetch settles the page cannot know whether Clear MLS is
    // allowed, so the button stays disabled rather than flashing enabled.
    const [autopilotStateLoaded, setAutopilotStateLoaded] = useState(false);
    const clearBlockedByAutopilot = autopilotOn === true && autopilotBusy;
    const [leaveOverlay, setLeaveOverlay] = useState(false);
    // Set when the user confirms "Leave" on a browser back/forward attempt: it drops the guard
    // (count → 0) so the pending navigation can complete (see the effect below).
    const [forceLeave, setForceLeave] = useState(false);

    useEffect(() => {
        const loadAutopilotState = async (): Promise<void> => {
            try {
                const headers = await getAuthHeader();
                const { data } = await axios.get(
                    `${backendUrl}/settings/autopilot`,
                    { headers }
                );
                setAutopilotOn(Boolean(data?.settings?.enabled));
                setAutopilotBusy(data?.busy === true);
            } catch {
                setAutopilotOn(null);
                setAutopilotBusy(false);
            } finally {
                setAutopilotStateLoaded(true);
            }
        };
        loadAutopilotState();
    }, [backendUrl]);

    useEffect(() => {
        if (!clearBlockedByAutopilot) return;
        const id = window.setInterval(async () => {
            try {
                const headers = await getAuthHeader();
                const { data } = await axios.get(
                    `${backendUrl}/settings/autopilot`,
                    { headers }
                );
                setAutopilotOn(Boolean(data?.settings?.enabled));
                setAutopilotBusy(data?.busy === true);
            } catch (error) {
                console.error("Failed to re-check Auto Pilot state:", error);
            }
        }, AUTOPILOT_BUSY_POLL_MS);
        return () => window.clearInterval(id);
    }, [clearBlockedByAutopilot, backendUrl]);

    useEffect(() => {
        if (!hasMlsAccount) {
            setLoadingPrefs(false);
            return;
        }
        const load = async (): Promise<void> => {
            try {
                setLoadingPrefs(true);
                const { autoIngestEnabled: enabled, options, selected } =
                    await getMlsPreferences();
                setAutoIngestEnabled(enabled);
                setPropertyTypeOptions(options);
                setSelectedTypes(new Set(selected));
                savedRef.current = { autoIngestEnabled: enabled, selected };
            } catch {
                toast.error(
                    <CustomToast
                        message="Failed to load MLS sync settings"
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                setLoadingPrefs(false);
            }
        };
        load();
    }, [hasMlsAccount]);

    const isDirty =
        !loadingPrefs &&
        (autoIngestEnabled !== savedRef.current.autoIngestEnabled ||
            !sameTokens(savedRef.current.selected, selectedTypes));

    // Guard ALL in-app navigation away from this screen while there are unsaved changes: the
    // shared hook intercepts programmatic navigate() (Back button, sidebar/top-bar links) AND
    // the browser back/forward buttons, and opens the confirm dialog instead. `beforeunload`
    // additionally covers a hard refresh / tab close (which the SPA hook can't).
    //
    // NOTE: the hook pushes its browser-back "sentinel" only when its effect re-runs with
    // count > 0, and that effect keys on `onNavigationAttempt`. So this callback MUST change
    // when `isDirty` flips (hence the [isDirty] dep) — that re-runs the effect exactly once per
    // dirty transition and arms the back-button guard. A stable ([]) callback would leave the
    // browser-back button unguarded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onNavigationAttempt = useCallback(() => setLeaveOverlay(true), [isDirty]);
    const { pendingNavigation, confirmNavigation, cancelNavigation } =
        useUploadNavigationGuard({
            // Embedded (dialog) mode is read-only and has no route to guard.
            uploadCount: isDirty && !forceLeave && !embedded ? 1 : 0,
            onNavigationAttempt,
        });

    // Embedded mode is read-only, so `isDirty` can never be true there and the
    // overlay has nothing to lose on dismissal — no leave-confirm needed.

    // Browser back/forward doesn't carry a target, so confirmNavigation() can't complete it.
    // When the user confirms "Leave" on a back-button attempt we drop the guard (forceLeave →
    // count 0) and then navigate to the parent Settings page here.
    useEffect(() => {
        if (forceLeave) {
            if (embedded) {
                onClose?.();
            } else {
                navigate("/settings");
            }
        }
    }, [forceLeave, navigate, embedded, onClose]);

    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent): void => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const toggleType = (token: string): void => {
        if (!canEdit) return;
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(token)) {
                next.delete(token);
            } else {
                next.add(token);
            }
            return next;
        });
    };

    const handleSave = async (): Promise<void> => {
        if (!canEdit || saving) return;
        // Auto-syncing zero property types is a no-op, so require at least one when it's on.
        if (autoIngestEnabled && selectedTypes.size === 0) {
            toast.error(
                <CustomToast
                    message="Select at least one property type to auto-sync"
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        try {
            setSaving(true);
            const previousTypes = new Set(savedRef.current.selected);
            const { autoIngestEnabled: enabled, selected } =
                await updateMlsPreferences(
                    autoIngestEnabled,
                    Array.from(selectedTypes)
                );
            setAutoIngestEnabled(enabled);
            setSelectedTypes(new Set(selected));
            savedRef.current = { autoIngestEnabled: enabled, selected };
            if (user) {
                dispatch(setUser({ ...user, autoIngestEnabled: enabled }));
            }
            toast.success(
                <CustomToast
                    message="MLS sync settings saved"
                    type="success"
                />,
                { autoClose: 3000 }
            );
            // Re-run discovery now so a newly ticked type (e.g. opt-in commercial)
            // reaches the review list without waiting for the 15-minute sync.
            // Best-effort: the sync covers a failure here.
            const typesChanged =
                selected.length !== previousTypes.size ||
                selected.some((token) => !previousTypes.has(token));
            if (enabled && hasMlsAccount && typesChanged) {
                try {
                    const { pending } = await refreshMlsCandidates();
                    if (pending > 0) {
                        toast.info(
                            <CustomToast
                                message={`${pending} listing${pending === 1 ? "" : "s"} of your selected property types ${pending === 1 ? "is" : "are"} waiting in the review list on the Listings page.`}
                                type="info"
                            />,
                            { autoClose: 6000 }
                        );
                    }
                } catch {
                    // The 15-minute sync stages them anyway.
                }
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to save MLS sync settings"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setSaving(false);
        }
    };

    const handleClearMlsDetails = async (): Promise<void> => {
        try {
            setClearing(true);
            const headers = await getAuthHeader();
            const { data } = await axios.patch(
                `${backendUrl}/settings/mls-details/clear`,
                {},
                { headers }
            );
            if (user) {
                dispatch(setUser({ ...user, ...data }));
            }
            // Reset staged + saved so nothing lingers as "unsaved" after the account is cleared.
            setAutoIngestEnabled(false);
            setSelectedTypes(new Set());
            savedRef.current = { autoIngestEnabled: false, selected: [] };
            setClearOverlay(false);
            toast.success(
                <CustomToast message="MLS details cleared" type="success" />
            );
            // Clearing force-disabled autopilot server-side; run its cleanup
            // now so managed listings don't linger until the daily sweep.
            try {
                const cleanup = await axios.post(
                    `${backendUrl}/autopilot/disable-cleanup`,
                    {},
                    { headers }
                );
                const removed = cleanup.data?.removed ?? 0;
                if (removed > 0) {
                    toast.success(
                        <CustomToast
                            message={`Auto Pilot was turned off — ${removed} listing${
                                removed === 1 ? "" : "s"
                            } it managed ${
                                removed === 1 ? "was" : "were"
                            } removed.`}
                            type="success"
                        />,
                        { autoClose: 5000 }
                    );
                }
            } catch (cleanupError) {
                console.error(
                    "Autopilot cleanup after MLS clear failed:",
                    cleanupError
                );
            }
            setAutopilotOn(false);
        } catch (error) {
            const refused =
                axios.isAxiosError(error) &&
                error.response?.status === 423 &&
                error.response.data?.message;
            // A 423 proves Auto Pilot is on and setting up, whatever the
            // earlier state fetch said - disable the button right away.
            if (refused) {
                setAutopilotOn(true);
                setAutopilotBusy(true);
            }
            toast.error(
                <CustomToast
                    message={
                        refused
                            ? String(error.response?.data.message)
                            : "Failed to clear MLS details"
                    }
                    type="error"
                />
            );
        } finally {
            setClearing(false);
        }
    };

    return (
        <Card
            className={cn(
                "w-full max-w-4xl mx-auto",
                isMobile ? "h-full m-0 rounded-none" : "h-auto"
            )}
        >
            <div className="p-4">
                <Button
                    id="back-to-settings-btn"
                    onClick={() =>
                        embedded ? onClose?.() : navigate("/settings")
                    }
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" />{" "}
                    {embedded ? "Back to Setup" : "Back to Settings"}
                </Button>
            </div>
            {hasMlsAccount && (
                <CardHeader className="mb-0 pb-0">
                    <CardTitle>MLS Agent Profile</CardTitle>
                    <CardDescription>
                        These details were captured from your MLS listings and
                        are used to identify your account and automatically sync
                        active listings.
                    </CardDescription>
                </CardHeader>
            )}

            <CardContent className="space-y-6">
                {!hasMlsAccount ? (
                    <div className="flex flex-col items-center text-center gap-3 py-8">
                        <Building2 className="h-10 w-10 text-gray-400" />
                        <h3 className="text-lg font-semibold text-primary">
                            No MLS account connected
                        </h3>
                        <p className="text-sm text-gray-500 max-w-md">
                            Import one of your active MLS listings to connect a
                            new MLS account and enable automatic listing
                            synchronization.
                        </p>
                        <Button
                            onClick={() =>
                                embedded
                                    ? onClose?.()
                                    : navigate("/create-listing")
                            }
                            disabled={!isMlsApproved}
                            className={cn(
                                "mt-2",
                                !isMlsApproved &&
                                    "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Add Listing to Connect MLS
                        </Button>
                        {!isMlsApproved && (
                            <p className="text-xs text-gray-400">
                                Available once your MLS access is approved.
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        <ReadOnlyField
                            label="MLS Agent ID"
                            value={user?.listAgentMlsId}
                        />
                        <ReadOnlyField
                            label="Agent Name"
                            value={user?.listAgentFullName}
                        />
                        <ReadOnlyField
                            label="Office Name"
                            value={user?.listOfficeName}
                        />
                        <ReadOnlyField
                            label="MLS System"
                            value={user?.listAOR}
                        />

                        {/* The setup overlay is identity-only: the sync
                            preferences below are read-only there (canEdit is
                            false when embedded), so they'd render as dead
                            controls. They're configured after setup from
                            Settings -> MLS Agent Profile. */}
                        {!embedded && (
                            <>
                                <div className="flex items-center justify-between border-t border-gray-100 pt-6">
                                    <div className="flex flex-col flex-[0.8]">
                                        <Label
                                            htmlFor="auto-ingest"
                                            className="font-semibold text-primary"
                                        >
                                            Auto-Sync My MLS Listings
                                        </Label>
                                        <span className="text-sm text-gray-500 mt-2">
                                            Automatically sync new active listings
                                            associated with this MLS Agent ID as they
                                            become available.
                                        </span>
                                    </div>
                                    {loadingPrefs ? (
                                        <div className="w-12 h-6 bg-gray-200 rounded-full animate-skeleton"></div>
                                    ) : (
                                        <Switch
                                            onClick={() => {
                                                if (canEdit) {
                                                    setAutoIngestEnabled((v) => !v);
                                                }
                                            }}
                                            checked={autoIngestEnabled}
                                            disabled={!canEdit}
                                            id="auto-ingest"
                                        />
                                    )}
                                </div>

                                <MlsPropertyTypesSection
                                    options={propertyTypeOptions}
                                    selected={selectedTypes}
                                    onToggle={toggleType}
                                    canEdit={canEdit}
                                    loading={loadingPrefs}
                                />

                                <div
                                    className={cn(
                                        "flex gap-3 border-t border-gray-100 pt-6",
                                        isMobile
                                            ? "flex-col"
                                            : "flex-row items-center justify-between"
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        {saving && (
                                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                        )}
                                        <Button
                                            onClick={handleSave}
                                            disabled={
                                                !canEdit || saving || loadingPrefs
                                            }
                                            className={cn(isMobile && "w-full")}
                                        >
                                            Save Changes
                                        </Button>
                                    </div>
                                    <SimpleTooltip
                                        message={CLEAR_MLS_BUSY_TOOLTIP}
                                        visible={clearBlockedByAutopilot}
                                        className={cn(
                                            "shrink-0",
                                            isMobile && "w-full"
                                        )}
                                    >
                                        <Button
                                            onClick={() => setClearOverlay(true)}
                                            disabled={
                                                !canEdit ||
                                                clearing ||
                                                !autopilotStateLoaded ||
                                                clearBlockedByAutopilot
                                            }
                                            variant="destructive"
                                            className={cn(
                                                "shrink-0",
                                                isMobile && "w-full",
                                                clearBlockedByAutopilot &&
                                                    "pointer-events-none"
                                            )}
                                        >
                                            Clear MLS Details
                                        </Button>
                                    </SimpleTooltip>
                                </div>
                            </>
                        )}
                        {!isMlsApproved && (
                            <p className="text-xs text-gray-400">
                                Available once your MLS access is approved.
                            </p>
                        )}
                    </>
                )}
            </CardContent>

            <ReconfirmOverlay
                isOpen={clearOverlay}
                onClose={() => setClearOverlay(false)}
                onConfirm={handleClearMlsDetails}
                title="Clear MLS Details?"
                message="This removes your saved MLS agent and stops new active listings from syncing automatically."
                messageTwo={
                    autopilotOn === true
                        ? "Auto Pilot will also be turned OFF — every listing it manages, including its campaigns and posts, will be permanently deleted. This cannot be undone. Listings not managed by Auto Pilot won't be affected."
                        : autopilotOn === false
                        ? "Your existing listings won't be affected."
                        : "If Auto Pilot is on, it will also be turned off — the listings it manages, including their campaigns and posts, will be permanently deleted. Listings not managed by Auto Pilot won't be affected."
                }
                confirmText="Clear MLS Details"
                cancelText="Cancel"
                reverse={true}
            />

            <ReconfirmOverlay
                isOpen={leaveOverlay}
                onClose={() => {
                    setLeaveOverlay(false);
                    cancelNavigation();
                }}
                onConfirm={() => {
                    setLeaveOverlay(false);
                    // Programmatic nav (sidebar / Back button) carries a target the hook can
                    // complete; browser back/forward doesn't, so fall back to forceLeave.
                    if (pendingNavigation) {
                        confirmNavigation();
                    } else {
                        setForceLeave(true);
                    }
                }}
                title="Leave without saving?"
                message="You have unsaved changes to your MLS sync settings."
                messageTwo="If you leave now, your changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                reverse={true}
            />
        </Card>
    );
};

export default MlsDetails;
